import { useState, FormEvent } from "react";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

export default function ContactSection() {
  const [ref, visible] = useScrollAnimation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      await fetch("https://readdy.ai/api/form/d6spcgg37h4e5cfhpsd0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#f9f9f9] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — Info */}
          <div
            ref={ref}
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p
              className="text-black/40 text-xs tracking-[0.3em] uppercase mb-3"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              Get in Touch
            </p>
            <h2
              className="text-black text-5xl md:text-6xl font-black uppercase leading-none mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Contact Us
            </h2>
            <p
              className="text-black/55 text-sm leading-relaxed mb-10 max-w-sm"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              Got a question, catering inquiry, or just want to say hello? We&apos;d love to hear from you.
              Fill out the form and we&apos;ll get back to you as soon as possible.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <ContactDetail
                icon="ri-map-pin-2-line"
                label="Location"
                value="Greater Bay Area"
              />
              <ContactDetail
                icon="ri-time-line"
                label="Hours"
                value="Mon – Fri: 10am – 8pm"
                sub="Sat – Sun: 9am – 7pm"
              />
            </div>

            {/* Social */}
            <div className="flex items-center gap-4 mt-10 pt-8 border-t border-black/10">
              <p
                className="text-black/30 text-xs tracking-[0.2em] uppercase"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                Follow us
              </p>
              {["ri-instagram-line", "ri-facebook-line", "ri-twitter-x-line"].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-9 h-9 border border-black/15 rounded-full flex items-center justify-center text-black/40 hover:bg-black hover:text-white hover:border-black transition-all duration-200 cursor-pointer"
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {submitted ? (
              <div className="border border-black/10 rounded-sm p-12 text-center bg-white">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-white text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3
                  className="text-black text-2xl font-black uppercase mb-3"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Message Sent!
                </h3>
                <p
                  className="text-black/50 text-sm"
                  style={{ fontFamily: "'Barlow', sans-serif" }}
                >
                  Thanks for reaching out. We&apos;ll be in touch shortly.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-us-form"
                onSubmit={handleSubmit}
                className="bg-white border border-black/10 rounded-sm p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <FormField label="First Name" name="firstName" placeholder="John" required />
                  <FormField label="Last Name" name="lastName" placeholder="Doe" required />
                </div>
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  required
                />
                <div>
                  <label
                    className="block text-black/60 text-[11px] tracking-[0.2em] uppercase font-bold mb-2"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  >
                    Subject
                  </label>
                  <select
                    name="subject"
                    required
                    className="w-full border border-black/15 rounded-sm px-4 py-3 text-sm text-black/70 focus:outline-none focus:border-black transition-colors duration-200 bg-white cursor-pointer"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  >
                    <option value="">Select a topic</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Catering">Catering</option>
                    <option value="Feedback">Feedback</option>
                    <option value="Press & Media">Press &amp; Media</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className="block text-black/60 text-[11px] tracking-[0.2em] uppercase font-bold"
                      style={{ fontFamily: "'Barlow', sans-serif" }}
                    >
                      Message
                    </label>
                    <span
                      className={`text-[11px] ${charCount > 500 ? "text-red-500" : "text-black/30"}`}
                      style={{ fontFamily: "'Barlow', sans-serif" }}
                    >
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us what's on your mind..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-black/15 rounded-sm px-4 py-3 text-sm text-black/70 focus:outline-none focus:border-black transition-colors duration-200 resize-none"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full bg-black text-white text-xs tracking-[0.2em] uppercase font-bold py-4 rounded-sm hover:bg-black/80 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ fontFamily: "'Barlow', sans-serif" }}
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line text-sm animate-spin w-4 h-4 flex items-center justify-center"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line text-sm w-4 h-4 flex items-center justify-center"></i>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({
  label,
  name,
  type = "text",
  placeholder,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label
        className="block text-black/60 text-[11px] tracking-[0.2em] uppercase font-bold mb-2"
        style={{ fontFamily: "'Barlow', sans-serif" }}
      >
        {label}
      </label>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        required={required}
        className="w-full border border-black/15 rounded-sm px-4 py-3 text-sm text-black/70 focus:outline-none focus:border-black transition-colors duration-200"
        style={{ fontFamily: "'Barlow', sans-serif" }}
      />
    </div>
  );
}

function ContactDetail({
  icon,
  label,
  value,
  sub,
}: {
  icon: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-9 h-9 border border-black/12 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
        <i className={`${icon} text-black/50 text-sm w-4 h-4 flex items-center justify-center`}></i>
      </div>
      <div>
        <p
          className="text-black/30 text-[10px] tracking-[0.2em] uppercase mb-0.5"
          style={{ fontFamily: "'Barlow', sans-serif" }}
        >
          {label}
        </p>
        <p
          className="text-black text-sm font-medium"
          style={{ fontFamily: "'Barlow', sans-serif" }}
        >
          {value}
        </p>
        {sub && (
          <p
            className="text-black/50 text-sm"
            style={{ fontFamily: "'Barlow', sans-serif" }}
          >
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}
